import axios, {
  AxiosError,
  AxiosHeaders,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from "axios";
import { tokenStore } from "./tokenStore";

// Base URL: env override in prod (VITE_API_URL), else the same-origin "/api" proxy
// (vite dev proxy → VITE_API_PROXY_TARGET || http://localhost:8000).
const baseURL = import.meta.env.VITE_API_URL ?? "/api";

export const api = axios.create({
  baseURL,
  headers: { "Content-Type": "application/json" },
});

// Request interceptor: attach the access token if present. Null-safe — before login
// (or after logout) there is simply no Authorization header.
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const access = tokenStore.getAccess();
  if (access) {
    config.headers = AxiosHeaders.from(config.headers);
    config.headers.set("Authorization", `Bearer ${access}`);
  }
  // File uploads send a FormData body. The instance default Content-Type is
  // application/json, which makes axios serialize FormData to JSON (-> backend
  // replies 415 Unsupported Media Type because it expects multipart). Drop the
  // header here so the browser sets `multipart/form-data` WITH its boundary.
  // Runs before transformRequest, so the JSON serialization never happens.
  if (typeof FormData !== "undefined" && config.data instanceof FormData) {
    config.headers = AxiosHeaders.from(config.headers);
    config.headers.delete("Content-Type");
  }
  return config;
});

// ---- 401 -> refresh -> retry (single-flight) ----
// SimpleJWT refresh endpoint. We de-dupe concurrent refreshes so a burst of 401s
// triggers exactly one refresh call; queued requests await the same promise.
let refreshPromise: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  const refresh = tokenStore.getRefresh();
  if (!refresh) throw new Error("no-refresh-token");
  // Bare axios (not `api`) to avoid the interceptor recursing on this call.
  const res = await axios.post<{ access: string; refresh?: string }>(
    `${baseURL}/users/refresh`,
    { refresh },
  );
  const newAccess = res.data.access;
  tokenStore.setAccess(newAccess);
  if (res.data.refresh) tokenStore.setTokens(newAccess, res.data.refresh);
  return newAccess;
}

interface RetriableConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as (RetriableConfig & InternalAxiosRequestConfig) | undefined;
    const status = error.response?.status;

    // Only attempt refresh once per request, and never for the refresh call itself.
    const isRefreshCall = original?.url?.includes("/users/refresh");
    if (status === 401 && original && !original._retry && !isRefreshCall) {
      original._retry = true;
      try {
        refreshPromise = refreshPromise ?? refreshAccessToken();
        const newAccess = await refreshPromise;
        refreshPromise = null;
        original.headers = AxiosHeaders.from(original.headers);
        original.headers.set("Authorization", `Bearer ${newAccess}`);
        return api(original);
      } catch (refreshErr) {
        refreshPromise = null;
        // Refresh failed → session is dead. Clear tokens and bounce to login.
        tokenStore.triggerLogout();
        return Promise.reject(refreshErr);
      }
    }
    return Promise.reject(error);
  },
);
